import { Actions, Prefs, Store, Textures } from '@types'
import getStandardMaterial from '@utils/conversion/getStandardMaterial'
import { toVector3 } from '@utils/conversion/toVector3'
import { deepDiff } from '@utils/deepDiff'
import { enableMapSet } from 'immer'
import { mountStoreDevtool } from 'simple-zustand-devtools'
import { OBB } from 'three/examples/jsm/math/OBB'
import type { TemporalState } from 'zundo'
import { temporal } from 'zundo'
import { StoreApi, UseBoundStore, create, useStore } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { createAreaActions } from './areaActions'
import {
  BASE_CAMERA_OBB_SIZE,
  baseCamera2DData,
  baseCamera3DData,
  baseLayers,
  baseTextos,
  emptyClientInfo,
  lastView,
} from './baseValues'
import { createFreeCotaActions } from './freeCotaActions'
import { createHiddenWallsActions } from './hiddenWallActions'
import { createHoleActions } from './holeActions'
import { createItemActions } from './itemActions'
import { createLightActions } from './lightActions'
import { createLinkedItemsActions } from './linkedItemsActions'
import { createPartitionsActions } from './partitionActions'
import { createProjectActions } from './projectActions'
import { createSceneActions } from './sceneActions'
import { createWallsActions } from './wallActions'

enableMapSet()

type ResourceStore = {
  resources: Map<string, boolean | undefined>
  setResource: (id: string, state?: boolean) => void
  isLoaded: boolean
}

export const resourceStore = create<ResourceStore>(
  immer((set, get) => ({
    resources: new Map(),
    setResource: (id: string, state?: boolean) => {
      set((store: ResourceStore) => {
        store.resources.set(id, state)
      })
    },
    isLoaded: () => {
      const resources = get().resources
      if (!resources.size) return false
      return Array.from(resources.values()).every(Boolean)
    },
  })),
)
export const setResource = resourceStore.getState().setResource
getStandardMaterial

type TipoUsuario = 'G' | 'A' | 'U' | string // Puedes ajustar según tus tipos

export const appStoreBase = create<
  Store & Actions & { tipoUsuario: TipoUsuario | null; setTipoUsuario: (tipo: TipoUsuario) => void }
>()(
  temporal(
    immer((set, get) => ({
      mode: 'MODE_IDLE',
      lastThickness: 150,
      areas: new Map(),
      currentObjectID: '',
      currentFirstWall: '',
      idiomaCultura: 'ES',
      holeInfo: {
        mano: null,
        bisagras: null,
        combinaciones: null,
      },
      price: 0,
      itemsCopyId: '',
      isModalOpen: false,
      languageOptions: [] as Store['languageOptions'],
      vertexBeingDragged: ['', ''],
      currentArea: '',
      camera3DData: baseCamera3DData,
      camera2DPosition: baseCamera2DData,
      shouldCameraMove: true,
      prefsInfo: [],
      prefs: {} as Prefs,
      textos: baseTextos,
      clientInfo: emptyClientInfo,
      pendingDesignerInfo: null,
      currentLayer: 0,
      increment: 1,
      storeHistory: '' as any as Store,
      selectedObjects: new Map(),
      layers: baseLayers,
      items: new Map(),
      freeMeasurements: new Map(),
      freeCotaStartPoint: null,
      freeCotaPreviewEnd: null,
      selectedFreeCotaId: null,
      selectedFreeCotaClickPosition: null,
      sceneHistory: [],
      sceneGLB: new Map(),
      sceneBoundingBoxes: new Map(),
      collisionableItems: new Set(),
      selectedRoom: null,
      softwareSignature: 'For2Home 2.0.6',
      ...createProjectActions(set, get),
      ...createWallsActions(set, get),
      ...createPartitionsActions(set, get),
      ...createHiddenWallsActions(set, get),
      ...createSceneActions(set, get),
      ...createItemActions(set, get),
      ...createLightActions(set, get),
      ...createAreaActions(set, get),
      ...createHoleActions(set, get),
      ...createLinkedItemsActions(set, get),
      ...createFreeCotaActions(set, get),
      isSaving: false,
      setIsSaving: (value: boolean) => set({ isSaving: value }),
      isTransitioningTo3D: false,
      setIsTransitioningTo3D: (value: boolean) => set({ isTransitioningTo3D: value }),
      canDraw: true,
      texture1: null as unknown as Textures[number],
      texture2: null as unknown as Textures[number],
      texture3: null as unknown as Textures[number],
      texture4: null as unknown as Textures[number],
      impuestos: [],
      isPathTracing: false,
      isOutlineMode: false,
      setIsOutlineMode: (value: boolean) => set({ isOutlineMode: value }),
      cameraOBB: new OBB(toVector3([0, 0, 0]), BASE_CAMERA_OBB_SIZE),
      shouldUpdateCamera: true,
      cameraCenter: [0, 4000, 0],
      isProjectOpened: false,
      frameloop: 'demand',
      savedViews: [],
      lastView: lastView,
      removedLightsByProject: new Map(),
      tipoUsuario: null,
      setTipoUsuario: (tipo: TipoUsuario) => set({ tipoUsuario: tipo }),
      idTiendas: null,
      setIdTiendas: (id: number | null) => set({ idTiendas: id }),
      disVerdis: false,
      setDisVerdis: (v: boolean) => set({ disVerdis: v }),
      disVerUsu: false,
      setDisVerUsu: (v: boolean) => set({ disVerUsu: v }),
      disVerGeneral: false,
      setDisVerGeneral: (v: boolean) => set({ disVerGeneral: v }),
      verPrecios: false,
      setVerPrecios: (v: boolean) => set({ verPrecios: v }),
      admModPermisos: false,
      setAdmModPermisos: (v: boolean) => set({ admModPermisos: v }),
      asignarDisenyosUf: false,
      setAsignarDisenyosUf: (v: boolean) => set({ asignarDisenyosUf: v }),
      esMio: true,
      contadorModificaciones: 0,
      isGlobalSpinnerVisible: false,
      globalSpinnerMessage: '',
      setGlobalSpinner: (visible: boolean, message?: string) =>
        set((s) => {
          s.isGlobalSpinnerVisible = visible
          if (message !== undefined) {
            s.globalSpinnerMessage = message
          }
        }),
      successMessage: null,
      setSuccessMessage: (message: string | null) => set({ successMessage: message }),
      frontalActive: false,
      setFrontalActive: (v: boolean) => set({ frontalActive: v }),
      frontalsAutoEnabled: true,
      setFrontalsAutoEnabled: (v: boolean) => set({ frontalsAutoEnabled: v }),
      lateralsAutoEnabled: true,
      setLateralsAutoEnabled: (v: boolean) => set({ lateralsAutoEnabled: v }),
      isDrawingFrontals: false,
      OpcionesFrontales: undefined,
      setOpcionesFrontales: (opciones: Record<string, string>) =>
        set({ OpcionesFrontales: opciones }),
      frontalesOptions: undefined,
      setFrontalesOptions: (opciones: Record<string, string>) =>
        set({ frontalesOptions: opciones }),
      frontalesTexturesOptions: undefined,
      setFrontalesTexturesOptions: (textures: any[]) => set({ frontalesTexturesOptions: textures }),
      // Performance mode (lightweight): lowers DPR, disables antialias/shadows, and turns off mesh shadows
      performanceMode: false,
      togglePerformanceMode: () =>
        set((s) => {
          s.performanceMode = !s.performanceMode
        }),

      toolbarMenuSearchTerm: null as string | null,
      setToolbarMenuSearchTerm: (term: string | null) => set({ toolbarMenuSearchTerm: term }),
      toolbarMenuToOpen: null as string | null,
      setToolbarMenuToOpen: (menu: string | null) => set({ toolbarMenuToOpen: menu }),
      toolbarBreadcrumbInit: null as Array<{
        texto: string
        i: number
        clavemenuabrir: string
      }> | null,
      setToolbarBreadcrumbInit: (
        breadcrumb: Array<{ texto: string; i: number; clavemenuabrir: string }> | null,
      ) => set({ toolbarBreadcrumbInit: breadcrumb }),
      toolbarAcabadoBlanco: null,
      setToolbarAcabadoBlanco: (value: boolean | null) => set({ toolbarAcabadoBlanco: value }),
      // Drag selection rectangle (coordenadas de pantalla)
      dragSelectionScreenStart: null,
      dragSelectionScreenEnd: null,
      isDragSelecting: false,
      hideDimentions3D: false,
      setHideDimentions3D: (v: boolean) => set({ hideDimentions3D: v }),
      // Modal de unidades faltantes
      unidadesFaltantes: [],
      mostrarModalUnidadesFaltantes: false,
      modalUnidadesMostrado: false, // Controla si ya se mostró el modal en este proyecto
      setMostrarModalUnidadesFaltantes: (mostrar: boolean) =>
        set({ mostrarModalUnidadesFaltantes: mostrar }),
      setModalUnidadesMostrado: (mostrado: boolean) => set({ modalUnidadesMostrado: mostrado }),
    })),
    {
      partialize: (state) => {
        const { areas, items, mode, layers, sceneBoundingBoxes } = state
        return {
          areas,
          items,
          mode,
          layers,
          sceneBoundingBoxes,
        }
      },
      limit: 5000,
      diff: (pastState, currentState) => {
        const myDiff = deepDiff(currentState, pastState)
        return myDiff
      },
    },
  ),
)
type WithSelectors<S> = S extends { getState: () => infer T }
  ? S & { use: { [K in keyof T]: () => T[K] } }
  : never

const createSelectors = <S extends UseBoundStore<StoreApi<object>>>(_store: S) => {
  const store = _store as WithSelectors<typeof _store>
  store.use = {}
  for (const k of Object.keys(store.getState())) {
    ;(store.use as any)[k] = () => store((s) => s[k as keyof typeof s])
  }
  return store
}

export const useTemporalStore = <T>(selector: (state: TemporalState<Store>) => T) =>
  useStore(useAppStore.temporal, selector)

export const useAppStore = createSelectors(appStoreBase)
if (
  (typeof import.meta !== 'undefined' && import.meta.env?.DEV) ||
  process.env.NODE_ENV === 'development'
) {
  mountStoreDevtool('Store', useAppStore)
  import('@utils/debug/compareLinkedItemMaterials')
}
